// Отгрузка — перенос раздела «🚚 Отгрузка» с портала 55.
//
// ГДЕ ЛЕЖАТ ДАННЫЕ, И ПОЧЕМУ НЕ В СМАРТ-ПРОЦЕССЕ 1188.
// На портале «Отгрузка» — это Service Blueprint по документам 1С УТ: заказ клиента → обеспечение
// → передача → закрытие. Заказ клиента и Реализация живут в 1С, и в них вся правда: что продано,
// что списано со склада, кому, откуда и когда.
//
// Смарт-процесс 1188 «Логист (отправка)» под этот раздел не годится, и это проверено на живых
// данных 26.07.2026: в ядре у него ШЕСТЬ записей, все от 01.08.2025, все с названием «ОТПРАВКА»,
// все на одну сделку 87451, с тестовыми значениями («Габариты: 111», трек «555-12341234»).
// Бизнес-процессов у 1188 нет вовсе — единственный, кто в него пишет, это портал 55, и то
// четыре поля (ТК, трек, куда, дата отгрузки) в момент заполнения формы логистики. Экран над
// этими шестью строками показывал бы, что отгрузок в компании не бывает.
//
// ─────────────────── ЧТО ИЗМЕНИЛОСЬ 27.07.2026: ЭКРАН ЧИТАЕТ ЗЕРКАЛО ───────────────────
// Раньше экран ходил в 1С через прокси и считал всё в браузере: тянул до 500 заказов
// и до 500 реализаций за окно, складывал суммы, искал по загруженному куску и по нему же
// решал, что «ждёт отгрузки». Три следствия, и ни одно не лечится кешем:
//   • «ждёт отгрузки» считалось ПО ОКНУ. Реализация к заказу могла лежать за его границей —
//     и заказ попадал в ожидающие, хотя товар уехал. По всей базе таких заказов
//     на 28.07.2026 — 362, а по одной только ссылке на заказ (как считал браузер) 410:
//     48 заказов закрыты реализацией той же сделки, и увидеть это можно лишь запросом
//     по всей базе;
//   • поиска по базе не было вовсе: искали по тому, что уже загружено;
//   • окно 180 дней упиралось в потолок 500 документов, и экран честно писал «сузьте окно» —
//     то есть признавался, что считает по неполным данным.
// Теперь всё это считает Postgres по зеркалу (ut_api.py, ручка /ut/shipping): один запрос
// на открытие вместо девяти, итоги и счётчики — по всей выборке, поиск — по всей базе.
//
// ЧТО ОСТАЛОСЬ НА ПРОКСИ К 1С, И ЭТО НЕ ЗАБЫТО, А НАРОЧНО:
//   • НАЗВАНИЯ склада, менеджера и клиента. В зеркале документов лежат только ссылки:
//     справочники 1С в Postgres не переносились. Названия берём теми же запросами, что и
//     раздел «Склад», из того же кеша ядра (склады живут 6 часов, менеджеры час);
//   • ПОИСК КЛИЕНТА по названию или ИНН. Контрагентов в 1С 6 901 на 28.07.2026, в зеркале
//     их нет — экран находит ссылки в справочнике и отдаёт их ядру фильтром;
//   • useShippingDocs (api/purchasing.ts) — прямое чтение документов из 1С. Экран им больше
//     не пользуется, но хук оставлен рабочим: он отвечает на другой вопрос — «что в 1С прямо
//     сейчас», без задержки на синк. Это нужно, когда синк встал или когда сверяют зеркало
//     с источником.
//
// ЭКРАН ТОЛЬКО ЧИТАЕТ, и это свойство конструкции: у прокси к 1С в ядре записи нет вовсе,
// а ручка /ut/* ходит в Postgres одними SELECT. Создание заказа, Реализация, перемещение и
// правка логистики остаются в 1С и на портале 55.
import { useMemo, useState } from 'react'

import { useContragentSearch, useUtNamesForPage, useUtRefNames } from '../api/purchasing'
import { useОтгрузка } from '../api/ut'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { Print1cButton } from '../components/Print1cButton'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtDays, fmtMoney, fmtText } from '../lib/format'
import { emptyUtNames } from '../lib/purchasing'
import {
  ОКНА, РАЗМЕР_СТРАНИЦЫ, оЖдущих, свежестьЗеркала, способРу, статусРу,
} from '../lib/ut'
import type { СтрокаОтгрузки, Вкладка } from '../lib/ut'

/**
 * Подписи колонок — один объект на весь экран.
 *
 * Шапка таблицы и строка (`Row`) — РАЗНЫЕ компоненты, и держать подписи в двух местах
 * нельзя: на мониторе подпись читается из `<thead>`, а на телефоне из `data-label` у
 * ячейки, поэтому разъехавшиеся копии не видны никому, кто смотрит только на монитор.
 * Тот же приём, что в `PaymentsTable.tsx` (const З).
 *
 * Первая колонка зависит от вкладки, и это не разные слова об одном, а разные документы
 * 1С: на «Отгружено» строка — Реализация, на «Ждёт отгрузки» — Заказ клиента.
 */
const подписи = (tab: Вкладка) => ({
  number: tab === 'shipped' ? '№ реализации' : '№ заказа',
  date: 'Дата',
  client: 'Клиент',
  sum: 'Сумма',
  warehouse: 'Склад',
  status: 'Статус',
  plan: 'Отгрузить к',
  order: 'По заказу',
  way: 'Как везём',
  address: 'Куда',
  deal: 'Сделка',
  manager: 'Менеджер',
})

export default function Shipping() {
  const [tab, setTab] = useState<Вкладка>('shipped')
  const [days, setDays] = useState<number>(30)
  const [search, setSearch] = useState('')
  const [client, setClient] = useState('')
  const [warehouse, setWarehouse] = useState('')
  const [onlyUnposted, setOnlyUnposted] = useState(false)
  const [page, setPage] = useState(0)

  // Поиск по клиенту идёт через справочник 1С: в документах (и в зеркале) лежит только
  // ссылка на контрагента, а контрагентов 6 901 на 28.07.2026 — по названию их не
  // отфильтровать ни локально, ни в Postgres, где справочника нет вовсе.
  const clientSearch = useContragentSearch(client)
  const clientRefs = useMemo(
    () => (client.trim().length >= 3 ? (clientSearch.data?.refs ?? []) : []),
    [client, clientSearch.data],
  )
  // Пока ссылки клиента не приехали, список не запрашиваем: без них ядро вернуло бы ВСЮ
  // выборку, и человек на секунду увидел бы чужие отгрузки под своим фильтром.
  const ждёмКлиента = client.trim().length >= 3 && clientSearch.isLoading

  const отгрузка = useОтгрузка({
    tab,
    days,
    q: search.trim() || undefined,
    client: clientRefs.length ? clientRefs.join(',') : undefined,
    warehouse: warehouse || undefined,
    unposted: onlyUnposted || undefined,
    start: page * РАЗМЕР_СТРАНИЦЫ,
    limit: РАЗМЕР_СТРАНИЦЫ,
  })
  const d = отгрузка.data

  // Названия — из справочников 1С через прокси. Справочника сделок среди них больше нет:
  // номер сделки Bitrix приходит уже разобранным из зеркала.
  const dicts = useUtRefNames()
  const names = dicts.data?.names ?? emptyUtNames()

  const rows = d?.rows ?? []
  // Названия контрагентов — только для показанной страницы (пачками по 12 ссылок).
  const pageNames = useUtNamesForPage(rows.map((r) => r.client_ref ?? ''), [])

  // Склады фильтра — факт по ВСЕЙ выборке, а не по загруженному куску: ядро возвращает
  // ссылки с числом документов, названия подставляем из справочника.
  const warehouses = useMemo(() => {
    const список = (d?.warehouses ?? []).map((w) => ({
      ref: w.ref, n: w.n, name: names.warehouse[w.ref] || '',
    }))
    // Без названия склад показываем куском ссылки: «—» в выпадающем списке выбрать нельзя,
    // а отфильтровать по такому складу человеку может быть нужно.
    return список.sort((a, b) => (a.name || a.ref).localeCompare(b.name || b.ref, 'ru'))
  }, [d?.warehouses, names.warehouse])

  const resetAnd = <T,>(setter: (v: T) => void) => (v: T) => { setPage(0); setter(v) }
  const changeTab = (t: Вкладка) => { setPage(0); setTab(t) }
  const всего = d?.total ?? 0
  const З = подписи(tab)

  return (
    <>
      <div className="page-head">
        <h1>Отгрузка</h1>
        <div className="spacer" />
        <span className="muted">зеркало 1С «Управление торговлей», только чтение</span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="hint">
          «Отгружено» — документы Реализации в 1С: товар списан со склада. «Ждёт отгрузки» —
          заказы клиента, по которым реализации нет. Даты, склады, способ доставки и адрес —
          из самих документов; трек-номеров и транспортной компании здесь нет (см. оговорку
          внизу). Считает ядро по всей базе документов, а не браузер по загруженному окну.
        </div>
        <div className="filters" style={{ marginTop: 12 }}>
          <button type="button" onClick={() => changeTab('shipped')} disabled={tab === 'shipped'}>
            Отгружено ({d?.counts.shipped ?? 0})
          </button>
          <button type="button" onClick={() => changeTab('pending')} disabled={tab === 'pending'}>
            Ждёт отгрузки ({d?.counts.pending ?? 0})
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        {/* Пять полей в столбец на телефоне закрывают собой весь список отгрузок, поэтому
            блок сворачивается; применённое перечислено в его заголовке. Переключатель
            вкладок остался выше и в блок НЕ входит: это кнопки, а не фильтры — свернуть их
            значило бы спрятать выбор между «Отгружено» и «Ждёт отгрузки». */}
        <FilterBar id="shipping">
          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="sh-days">Окно</label>
            <select id="sh-days" value={days}
                    onChange={(e) => resetAnd(setDays)(Number(e.target.value))}>
              {ОКНА.map((w) => <option key={w.дней} value={w.дней}>{w.ярлык}</option>)}
            </select>
          </div>

          <div className="f" style={{ flex: 2, minWidth: 230 }}>
            <label htmlFor="sh-q">Поиск</label>
            <input id="sh-q" value={search} placeholder="номер документа, № сделки, адрес"
                   onChange={(e) => resetAnd(setSearch)(e.target.value)} />
          </div>

          <div className="f" style={{ flex: 2, minWidth: 210 }}>
            <label htmlFor="sh-client">Клиент (по справочнику 1С)</label>
            <input id="sh-client" value={client} placeholder="от 3 символов или ИНН"
                   onChange={(e) => resetAnd(setClient)(e.target.value)} />
          </div>

          <div className="f" style={{ minWidth: 220 }}>
            <label htmlFor="sh-wh">Склад</label>
            <select id="sh-wh" value={warehouse}
                    onChange={(e) => resetAnd(setWarehouse)(e.target.value)}
                    disabled={warehouses.length === 0}>
              <option value="">любой</option>
              {warehouses.map((w) => (
                <option key={w.ref} value={w.ref}>
                  {w.name || w.ref.slice(0, 8)} ({w.n})
                </option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 200 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
              <input type="checkbox" checked={onlyUnposted} style={{ width: 'auto' }}
                     onChange={(e) => resetAnd(setOnlyUnposted)(e.target.checked)} />
              только непроведённые
            </label>
          </div>
        </FilterBar>

        {/* Поиск идёт по ВСЕЙ базе документов, а не по загруженной странице. Сказать это
            стоит прямо: раньше было наоборот, и человек может помнить старое поведение. */}
        {search.trim() && (
          <div className="hint" style={{ marginTop: 10 }}>
            Ищем по всей базе документов зеркала: по номеру документа, по адресу доставки и —
            если введено число из 4–7 цифр — по номеру сделки Bitrix.
            {d?.filters.deal_id ? ` Сейчас это понято как сделка № ${d.filters.deal_id}.` : ''}
          </div>
        )}
        {client.trim().length >= 3 && (
          <div className="hint" style={{ marginTop: 10 }}>
            {clientSearch.isLoading
              ? 'Ищу контрагента в 1С…'
              : `Под «${client.trim()}» в справочнике 1С нашлось контрагентов: ${clientRefs.length}.`}
            {' '}Фильтр применён по их ссылкам, а не по тексту: в документах названия клиента нет,
            только ссылка на справочник.
          </div>
        )}
        {client.trim().length > 0 && client.trim().length < 3 && (
          <div className="hint" style={{ marginTop: 10 }}>
            Для поиска клиента нужно минимум 3 символа — по одной букве 1С отдала бы полсправочника.
          </div>
        )}
        {/* Честно про объём выборки: в отличие от разделов над Bitrix, здесь сужения по
            человеку нет вовсе — не «мы решили показать всё», а «сузить нечем». */}
        <div className="hint" style={{ marginTop: 10 }}>
          Показаны документы всей компании, а не только ваши: в 1С нет поля «ответственный
          в Bitrix», и сузить выборку по человеку ядру нечем. Права на этот раздел такие же,
          как на «Склад» — он открыт всем вошедшим.
        </div>
      </div>

      <QueryState isLoading={отгрузка.isLoading} error={отгрузка.error}>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="page-head" style={{ marginBottom: 10 }}>
            <h2 style={{ margin: 0 }}>
              {tab === 'shipped' ? 'Отгружено' : 'Ждёт отгрузки'}
            </h2>
            <div className="spacer" />
            <span className="hint">
              {свежестьЗеркала(d?.mirror.age_min)}
              {отгрузка.isFetching ? ' · обновляю…' : ''}
            </span>
            <button type="button" style={{ marginLeft: 8 }}
                    onClick={() => void отгрузка.refetch()}>обновить</button>
          </div>
          <div className="tiles">
            <div className="tile">
              <div className="t-label">Документов</div>
              <div className="t-sum">{d?.totals.count ?? 0}</div>
              <div className="t-label">
                {tab === 'shipped'
                  ? `реализаций под условия · всего в окне ${d?.counts.shipped ?? 0}`
                  : `заказов под условия · всего ждёт ${d?.counts.pending ?? 0}`}
              </div>
            </div>
            <div className="tile">
              <div className="t-label">Сумма</div>
              <div className="t-sum">{fmtMoney(d?.totals.sum ?? 0)}</div>
              <div className="t-label">по всей выборке, не по странице</div>
            </div>
            <div className="tile">
              <div className="t-label">Проведено в 1С</div>
              <div className="t-sum">{d?.totals.posted ?? 0}</div>
              <div className="t-label">из {d?.totals.count ?? 0}</div>
            </div>
            {tab === 'pending' ? (
              <div className={`tile${d?.counts.late_orders ? ' hot' : ''}`}
                   title="Заказы, у которых плановая дата отгрузки уже прошла, а реализации нет.">
                <div className="t-label">Срок отгрузки прошёл</div>
                <div className="t-sum">{d?.counts.late_orders ?? 0}</div>
                <div className="t-label">заказов</div>
              </div>
            ) : (
              <div className="tile" title="Реализация оформлена не «на основании» заказа клиента.">
                <div className="t-label">Без ссылки на заказ</div>
                <div className="t-sum">{d?.counts.orphan_sales ?? 0}</div>
                <div className="t-label">из {d?.counts.shipped ?? 0} реализаций</div>
              </div>
            )}
          </div>

          {tab === 'pending' && (
            <div className="hint" style={{ marginTop: 10 }}>
              {оЖдущих(d)}
            </div>
          )}
          {dicts.data?.failed.length ? (
            <div className="notice" style={{ marginTop: 12, marginBottom: 0 }}>
              Справочники названий из 1С не пришли: {dicts.data.failed.join(', ')}. В этих
              колонках будет «—»: это «не смогли прочитать название», а не «в документе пусто».
              Сами документы читаются из зеркала и от доступности 1С не зависят.
            </div>
          ) : null}
        </div>

        <div className="card">
          {ждёмКлиента ? (
            <div className="empty">
              <div className="empty-title">Ищу контрагента в справочнике 1С…</div>
              <div className="empty-hint">
                Пока ссылки клиента не найдены, список не запрашиваем: без них ядро показало
                бы выборку без фильтра.
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="empty">
              <div className="empty-title">
                {tab === 'shipped'
                  ? 'Отгрузок под эти условия нет'
                  : 'Заказов, ждущих отгрузки, под эти условия нет'}
              </div>
              <div className="empty-hint">Расширьте окно или снимите фильтры.</div>
            </div>
          ) : (
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th className="nowrap">{З.number}</th>
                    {/* Печать листа из 1С. Только у реализаций: у заказа состава ещё нет. */}
                    <th className="nowrap">Печать</th>
                    <th className="nowrap">{З.date}</th>
                    <th>{З.client}</th>
                    <th className="num">{З.sum}</th>
                    <th>{З.warehouse}</th>
                    <th className="nowrap">{З.status}</th>
                    {tab === 'pending' && <th className="nowrap">{З.plan}</th>}
                    {tab === 'shipped' && <th className="nowrap">{З.order}</th>}
                    <th className="nowrap">{З.way}</th>
                    <th>{З.address}</th>
                    <th className="nowrap">{З.deal}</th>
                    <th className="nowrap">{З.manager}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <Row key={r.ref} r={r} tab={tab}
                         clientName={pageNames.data?.contragent[r.client_ref ?? '']}
                         warehouse={names.warehouse[r.warehouse_ref ?? ''] || ''}
                         manager={names.user[r.manager_ref ?? ''] || ''} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {всего > РАЗМЕР_СТРАНИЦЫ && (
            <div className="pager">
              <button disabled={page === 0} onClick={() => setPage(Math.max(0, page - 1))}>
                ← назад
              </button>
              <span className="muted">
                {page * РАЗМЕР_СТРАНИЦЫ + 1}
                –{Math.min((page + 1) * РАЗМЕР_СТРАНИЦЫ, всего)}
                {' из '}{всего}
              </span>
              <button disabled={d?.next === null || d?.next === undefined}
                      onClick={() => setPage(page + 1)}>
                вперёд →
              </button>
            </div>
          )}
        </div>
      </QueryState>

      <NotHere />
    </>
  )
}

function Row({ r, tab, clientName, warehouse, manager }: {
  r: СтрокаОтгрузки; tab: Вкладка; clientName?: string; warehouse: string; manager: string
}) {
  const day = r.date ? r.date.slice(0, 10) : ''
  const plan = r.ship_plan_date ? r.ship_plan_date.slice(0, 10) : ''
  // Тот же объект, что и у шапки: на телефоне шапки нет, и подпись ячейке даёт `data-label`.
  const З = подписи(tab)
  return (
    <tr>
      <td className="mono nowrap" data-label={З.number}>{r.number || <span className="muted">—</span>}</td>
      {/* «состав» — честное умолчание для УТ: официальный бланк 1137 из этой базы
          выходит с семью пустыми графами (моста УТ→БП нет), и лист сам об этом пишет. */}
      <td className="nowrap" data-label="Печать">
        {r.kind === 'sale'
          ? <Print1cButton ref1c={r.ref} база="ut" форма="состав" номер={r.number} />
          : <span className="muted">—</span>}
      </td>
      <td className="nowrap" data-label={З.date}>{day ? fmtDate(day) : <span className="muted">—</span>}</td>
      <td data-label={З.client}>{clientName || <span className="muted">—</span>}</td>
      <td className="num" data-label={З.sum}>{fmtMoney(r.sum ?? 0)}</td>
      <td data-label={З.warehouse}>{fmtText(warehouse)}</td>
      {/* Статус переводит lib/ut по картам из lib/purchasing — карта одна на оба раздела. */}
      <td className="nowrap" data-label={З.status}>
        <span className={`badge ${r.kind === 'sale' ? 'won' : 'work'}`}>
          {статусРу(r) || '—'}
        </span>
        {!r.posted && <div><span className="badge lose">не проведён</span></div>}
      </td>
      {tab === 'pending' && (
        <td className="nowrap" data-label={З.plan}>
          {plan ? fmtDate(plan) : <span className="muted">не указано</span>}
          {r.overdue && r.overdue_days !== null && (
            <div><span className="badge lose">{fmtDays(r.overdue_days)} назад</span></div>
          )}
        </td>
      )}
      {tab === 'shipped' && (
        <td className="mono nowrap" data-label={З.order}>
          {/* Три разных ответа, а не два. «Нет ссылки» означает, что реализацию оформили
              не «на основании» заказа, — а не что заказа не существует. */}
          {r.order_number || (r.order_ref
            ? <span className="muted" title="Ссылка на заказ есть, но самого заказа в зеркале нет">заказ не найден</span>
            : <span className="muted" title="Реализация оформлена не «на основании» заказа">нет ссылки</span>)}
        </td>
      )}
      <td className="nowrap" data-label={З.way}>{fmtText(способРу(r.way))}</td>
      <td title={r.address || undefined} data-label={З.address}>
        {r.address || <span className="muted">—</span>}
      </td>
      <td className="nowrap" data-label={З.deal}>
        <EntityLink kind="deal" id={r.deal_id ? String(r.deal_id) : ''} />
        {!r.deal_id && r.deal_text && (
          <span className="muted" title={`В справочнике сделок 1С здесь не номер, а текст: ${r.deal_text}`}>
            не номер
          </span>
        )}
      </td>
      <td className="nowrap" data-label={З.manager}>{fmtText(manager)}</td>
    </tr>
  )
}

function NotHere() {
  return (
    <div className="card">
      <h2>Чего здесь нет и почему</h2>
      <dl className="dl">
        <dt>Трек-номер, транспортная компания, водитель, доверенность</dt>
        <dd>
          В 1С этих полей нет, и портал 55 держит их в своём хранилище (Cloudflare KV, ключ
          <span className="mono"> ship:&lt;ref заказа&gt;</span>), дублируя в смарт-процесс 1188.
          Ядру доступен только 1188 — а в нём шесть тестовых записей от 01.08.2025 на одну
          сделку. Показывать по ним «чем везём» значило бы врать, поэтому колонки нет:
          «как везём» — это способ доставки из самого документа 1С.
        </dd>

        <dt>Позиции документа и остатки по ним</dt>
        <dd>
          Табличные части («Товары») в зеркало не переносились: ни этому разделу, ни «Пути
          сделки» позиции не нужны, а весят они больше самих шапок документов. Раздел
          отвечает на «что уехало, кому, откуда и когда»; «чего не хватает, чтобы отгрузить» —
          это карточка одного заказа, она делается отдельно. Остатки по позиции уже есть
          в разделе «Склад».
        </dd>

        <dt>Названия склада, клиента и менеджера — не из зеркала</dt>
        <dd>
          В зеркале документов лежат только ссылки 1С: справочники в Postgres не переносились.
          Названия приходят теми же запросами, что и в разделе «Склад», из кеша ядра. Поэтому
          при недоступной 1С документы, суммы и счётчики останутся на месте, а в колонках
          названий будет «—» — и это значит «не прочитали имя», а не «пусто в документе».
        </dd>

        <dt>Документ, которого ещё нет в зеркале</dt>
        <dd>
          Зеркало обновляет синк раз в несколько минут, поэтому документ, заведённый в 1С
          только что, появится здесь с задержкой. Возраст зеркала показан над таблицей. Прямое
          чтение 1С из ядра никуда не делось (прокси <span className="mono">/api/1c/ut/…</span>),
          но раздел им не пользуется: сверять зеркало с источником — отдельная задача, а не
          режим работы экрана.
        </dd>

        <dt>Создание заказа, Реализация, перемещение, правка логистики</dt>
        <dd>
          Это запись в 1С. У прокси ядра её нет вовсе: наверх идёт одна функция и только GET.
          Такие действия остаются в 1С и на портале 55.
        </dd>

        <dt>Стадии Service Blueprint портала</dt>
        <dd>
          Портал рисует четыре шага (заказ → обеспечение → передача → закрытие) и считает
          «блокеры» по остаткам каждой позиции. Здесь шаг виден грубее — по наличию
          Реализации и по её проведённости: это то, что читается без обхода позиций.
        </dd>

        <dt>УПД и закрывающие документы</dt>
        <dd>
          Они в базе бухгалтерии (1С БП). Прокси ядра её выпускает, но только администратору
          и бухгалтерии: «своих строк» в бухгалтерской базе нет, сузить выборку по человеку
          нечем. Поэтому в общий раздел отгрузки они не входят.
        </dd>
      </dl>
    </div>
  )
}
